import {
  Sidebar,
  SidebarBody,
  SidebarHeading,
  SidebarLabel,
  SidebarSection,
  SidebarSpacer,
} from '@accure/ui';
import { HomeIcon, PlusIcon } from '@heroicons/react/24/solid';

import { PageUrlKey } from '@/entities/page-url-key';
import {
  type FeatureFlagChecker,
  useFeatureFlag,
} from '@/hooks/use-feature-flag';
import { useMe } from '@/hooks/use-me';
import { useWarrantyVersion } from '@/hooks/use-warranty-version';
import { cls } from '@/utils/cls';
import { getNavItemName } from '@/utils/get-nav-item-name';

import { AppSidebarFooter } from './app-sidebar-footer';
import { AppSidebarInternalIcon } from './app-sidebar-internal-icon';
import { AppSidebarItem } from './app-sidebar-item';
import { SiteSelection } from './site-selection';
import { VehicleSelection } from './vehicle-selection';

export type AppSidebarGlobalProps = {
  className?: string;
};
export const AppSidebarGlobal = ({ className }: AppSidebarGlobalProps) => {
  const me = useMe();
  const featureFlag = useFeatureFlag();
  const warrantyVersion = useWarrantyVersion();

  const shouldShowAnalyzeSection = (featureFlagChecker: FeatureFlagChecker) => {
    return (
      featureFlagChecker('alerts') ||
      featureFlagChecker('details') ||
      featureFlagChecker('taskmanager') ||
      featureFlagChecker('assets') ||
      featureFlagChecker('dataCompatibility') ||
      featureFlagChecker('sitemap')
    );
  };

  const shouldShowOrganizeSection = (
    featureFlagChecker: FeatureFlagChecker,
  ) => {
    return (
      featureFlagChecker('projectManagement') ||
      featureFlagChecker('communication') ||
      featureFlagChecker('records')
    );
  };

  return (
    <div className="h-full">
      <Sidebar className={cls('w-48 border-r pl-[2px]', className)}>
        <SidebarBody>
          {featureFlag('landingpage') ? (
            <SidebarSection>
              <AppSidebarItem to="/">
                <HomeIcon />
                <SidebarLabel>Home</SidebarLabel>
                <AppSidebarInternalIcon featureFlag="landingpage" />
              </AppSidebarItem>
            </SidebarSection>
          ) : null}
          <SidebarSection>
            {me.config.type === 'mobility' ? (
              <VehicleSelection />
            ) : (
              <SiteSelection />
            )}
          </SidebarSection>
          {shouldShowAnalyzeSection(featureFlag) && (
            <SidebarSection>
              <SidebarHeading>Analyze</SidebarHeading>
              {featureFlag('alerts') ? (
                <AppSidebarItem to={`/${PageUrlKey.ALERTS}`}>
                  {getNavItemName(PageUrlKey.ALERTS)}
                  <AppSidebarInternalIcon featureFlag="alerts" />
                </AppSidebarItem>
              ) : null}

              {featureFlag('taskmanager') ? (
                <AppSidebarItem to={`/${PageUrlKey.TASKS}`}>
                  {me.whiteLabel
                    ? 'Service Tasks'
                    : getNavItemName(PageUrlKey.TASKS)}
                  <AppSidebarInternalIcon featureFlag="taskmanager" />
                </AppSidebarItem>
              ) : null}

              {me.whiteLabel ? (
                <AppSidebarItem to="/service-content">
                  Service Content
                </AppSidebarItem>
              ) : null}

              {featureFlag('assets') ? (
                <AppSidebarItem to={`/${PageUrlKey.BENCHMARK}`}>
                  {getNavItemName(PageUrlKey.BENCHMARK)}
                  <AppSidebarInternalIcon featureFlag="assets" />
                </AppSidebarItem>
              ) : null}

              {warrantyVersion && me.config.type === 'mobility' ? (
                <AppSidebarItem to={`/${PageUrlKey.WARRANTY}`}>
                  {getNavItemName(PageUrlKey.WARRANTY)}
                  <AppSidebarInternalIcon
                    featureFlag={warrantyVersion.featureFlag}
                    tooltip={warrantyVersion.internalTooltip}
                  />
                </AppSidebarItem>
              ) : null}

              {featureFlag('dataCompatibility') ? (
                <AppSidebarItem to={`/${PageUrlKey.DATA_COMPATIBILITY}`}>
                  {getNavItemName(PageUrlKey.DATA_COMPATIBILITY)}
                  <AppSidebarInternalIcon featureFlag="dataCompatibility" />
                </AppSidebarItem>
              ) : null}

              {featureFlag('sitemap') && me.config.type !== 'mobility' ? (
                <AppSidebarItem to={`/${PageUrlKey.SITE_MAP}`}>
                  {getNavItemName(PageUrlKey.SITE_MAP)}
                  <AppSidebarInternalIcon featureFlag="sitemap" />
                </AppSidebarItem>
              ) : null}

              {featureFlag('systemLogs') && me.config.type !== 'mobility' ? (
                <AppSidebarItem to={`/${PageUrlKey.SYSTEM_LOGS}`}>
                  {getNavItemName(PageUrlKey.SYSTEM_LOGS)}
                  <AppSidebarInternalIcon featureFlag="systemLogs" />
                </AppSidebarItem>
              ) : null}
            </SidebarSection>
          )}
          {shouldShowOrganizeSection(featureFlag) && (
            <SidebarSection>
              <SidebarHeading>Organize</SidebarHeading>
              {featureFlag('projectManagement') ? (
                <AppSidebarItem to={`/${PageUrlKey.PROJECTS}`}>
                  {getNavItemName(PageUrlKey.PROJECTS)}
                  <AppSidebarInternalIcon featureFlag="projectManagement" />
                </AppSidebarItem>
              ) : null}

              {!me.whiteLabel && featureFlag('communication') ? (
                <AppSidebarItem to={`/${PageUrlKey.COMMUNICATION}`}>
                  {getNavItemName(PageUrlKey.COMMUNICATION)}
                  <AppSidebarInternalIcon featureFlag="communication" />
                </AppSidebarItem>
              ) : null}

              {featureFlag('records') ? (
                <AppSidebarItem to={`/${PageUrlKey.DOCUMENTS}`}>
                  {getNavItemName(PageUrlKey.DOCUMENTS)}
                  <AppSidebarInternalIcon featureFlag="records" />
                </AppSidebarItem>
              ) : null}
            </SidebarSection>
          )}

          {!featureFlag('landingpage') && featureFlag('recordsCreate') ? (
            <AppSidebarItem to="/new-asset">
              <PlusIcon />
              <SidebarLabel>New asset</SidebarLabel>
              <AppSidebarInternalIcon featureFlag="recordsCreate" />
            </AppSidebarItem>
          ) : null}

          {featureFlag('customDashboards') &&
          me.config.customDashboards.length > 0 ? (
            <SidebarSection>
              <SidebarHeading>Custom</SidebarHeading>
              {me.config.customDashboards.map(({ path, menuLabel }) => (
                <AppSidebarItem to={`/custom/${path}`} key={path}>
                  {menuLabel}
                  <AppSidebarInternalIcon featureFlag="customDashboards" />
                </AppSidebarItem>
              ))}
            </SidebarSection>
          ) : null}
          <SidebarSpacer />
          <AppSidebarFooter />
        </SidebarBody>
      </Sidebar>
    </div>
  );
};
